var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useEffect, useLayoutEffect } from 'react';
import { cx, css as emotionCss } from 'emotion';
import FocusTrap from 'focus-trap-react';
import ReactDOM from 'react-dom';
import { getChildByType, getChildrenByType, removeChildrenByType } from 'react-nanny';
import { Loader } from '@citrus/loader';
import { getAutomation, getDebug, getStyleNames, useCitrusDebug, useEventListener, } from '@citrus/runtime';
import { useTheme } from '@citrus/user';
import { capitalize, escapeKeyPressed, isNotNilOrEmpty, removeKey } from '@pcty/utils';
import { resetScrollbar, setScrollbar } from './_utils';
import { Content, ContentInner, DrawerHeader as DrawerHeaderImport, DrawerHeaderNested as DrawerHeaderNestedImport, Overlay, Drawer as XDrawer, DrawerNested, } from './Drawer.styled';
import { drawerPadding, getOpen, getSizeAndPosition, overlayOpen } from '../styles/legacy';
import { drawerPadding as drawerPaddingCitron, getOpen as getOpenCitrus2022, getSizeAndPosition as getSizeAndPositionCitrus2022, headerWrapperOverride, overlayOpen as overlayOpenCitrus2022, } from '../styles/citron';
export var drawerOverlayOpenBodyIdentifier = 'pcty-fa915ce-overlay-open';
var VERSION = '4.1.0';
var Drawer = function (_a) {
    var _b, _c, _d;
    var automationId = _a.automationId, children = _a.children, className = _a.className, css = _a.css, focusTrapProps = _a.focusTrapProps, id = _a.id, _e = _a.ignoreCancelActions, ignoreCancelActions = _e === void 0 ? false : _e, loading = _a.loading, _f = _a.onClose, onClose = _f === void 0 ? function () { return undefined; } : _f, open = _a.open, padding = _a.padding, portal = _a.portal, _g = _a.position, position = _g === void 0 ? 'right' : _g, _h = _a.size, size = _h === void 0 ? 'medium' : _h, nested = _a.__nested, _j = _a.__TYPE, TYPE = _j === void 0 ? 'Drawer' : _j, other = __rest(_a, ["automationId", "children", "className", "css", "focusTrapProps", "id", "ignoreCancelActions", "loading", "onClose", "open", "padding", "portal", "position", "size", "__nested", "__TYPE"]);
    var debug = useCitrusDebug();
    var isCitron = useTheme().citrus2022;
    var onClickOverlay = useCallback(function (e) {
        if (!open || ignoreCancelActions)
            return;
        onClose({ event: e, onOverlay: true });
    }, [ignoreCancelActions, onClose, open]);
    var handleKeyPress = useCallback(function (e) {
        if (!open || ignoreCancelActions)
            return;
        var target = e.target;
        if (escapeKeyPressed(e) &&
            id === target.id && // only handle for this id/element
            !window.citrusModalOpen &&
            !window.citrusFlyoutOpen) {
            onClose({ event: e, onOverlay: false });
            e.preventDefault();
        }
    }, [open, ignoreCancelActions, id, onClose]);
    useEventListener('keydown', handleKeyPress);
    // unmount
    useEffect(function () { return function () {
        resetScrollbar();
        document.body.classList.remove(drawerOverlayOpenBodyIdentifier);
    }; }, []);
    useLayoutEffect(function () {
        if (open) {
            document.body.classList.add(drawerOverlayOpenBodyIdentifier);
        }
        if (open && !nested) {
            setScrollbar();
            return;
        }
        if (!nested) {
            resetScrollbar();
            document.body.classList.remove(drawerOverlayOpenBodyIdentifier);
        }
    }, [open, nested]);
    var _getOpen = isCitron ? getOpenCitrus2022 : getOpen;
    var _getSizeAndPosition = isCitron ? getSizeAndPositionCitrus2022 : getSizeAndPosition;
    var emotionObj = (_b = {},
        _b[emotionCss(_getSizeAndPosition(size, position, !!open))] = true,
        _b[emotionCss(_getOpen(position))] = open,
        _b[emotionCss({ boxShadow: 'none' })] = nested,
        _b[emotionCss(headerWrapperOverride)] = isCitron,
        _b[emotionCss(css)] = isNotNilOrEmpty(css),
        _b);
    var classes = cx(emotionObj, className || '');
    var styleNames = (_c = {},
        _c[size + capitalize(position) + (open ? 'Open' : 'Closed')] = true,
        _c['open' + capitalize(position)] = open !== null && open !== void 0 ? open : false,
        _c);
    var overlayEmotionObj = (_d = {},
        _d[emotionCss(isCitron ? overlayOpenCitrus2022 : overlayOpen)] = !nested && open,
        _d);
    var overlayClasses = cx(overlayEmotionObj, className || '');
    var overlayStyleNames = {
        overlayOpen: emotionObj[emotionCss(isCitron ? overlayOpenCitrus2022 : overlayOpen)],
    };
    var Component = XDrawer;
    if (nested && isCitron) {
        Component = DrawerNested;
    }
    var DrawerHeader = DrawerHeaderImport;
    if (nested && isCitron) {
        DrawerHeader = DrawerHeaderNestedImport;
    }
    // Visibility style attribute allows screen readers to ignore drawer in dom
    var renderDrawer = function () { return (React.createElement(FocusTrap, __assign({ active: open, focusTrapOptions: { allowOutsideClick: true, initialFocus: "#".concat(id), returnFocusOnDeactivate: false } }, focusTrapProps),
        React.createElement("div", { style: { visibility: open ? 'visible' : 'hidden', transition: "visibility 0s ".concat(open ? 0 : 0.5, "s") } },
            React.createElement(Overlay, __assign({ className: overlayClasses, id: id + '-overlay', onClick: onClickOverlay, styleNames: getStyleNames(overlayStyleNames) }, getAutomation(automationId, 'drawer-overlay'))),
            React.createElement(Component, __assign({ className: classes, "data-target": "drawer", id: id, styleNames: getStyleNames(styleNames), tabIndex: 0 }, removeKey(other, 'onClose'), getAutomation(automationId, 'drawer-container'), getDebug(debug, TYPE, VERSION), { role: "dialog" }),
                React.createElement(DrawerHeader, __assign({}, getAutomation(automationId, 'drawer-header')), getChildrenByType(children, ['Header', 'ContentHeader'])),
                React.createElement(Content, __assign({}, getAutomation(automationId, 'drawer-content')),
                    getChildByType(children, ['DrawerSubheader', 'PctyDrawerSubheader']),
                    renderContent(children, padding, !!isCitron),
                    React.createElement(Loader, { global: false, hidden: !loading })))))); };
    if (!portal) {
        return renderDrawer();
    }
    return ReactDOM.createPortal(renderDrawer(), document.querySelector(typeof portal === 'string' ? portal : 'body'));
};
var renderContent = function (children, padding, isCitron) {
    var _a;
    var _children = removeChildrenByType(children, [
        'Header',
        'ContentHeader',
        'DrawerSubheader',
        'PctyContentHeader',
        'PctyDrawerSubheader',
    ]);
    var _padding = isCitron ? drawerPaddingCitron : drawerPadding;
    return (React.createElement(ContentInner, { className: padding ? emotionCss(_padding) : undefined, styleNames: getStyleNames((_a = {}, _a[emotionCss(_padding)] = padding, _a)) }, _children.map(function (child) {
        if (child.props && ['Drawer', 'PctyDrawer'].indexOf(child.props.__TYPE) >= 0)
            return React.cloneElement(child, { __nested: true });
        if (child.props && ['Filter', 'PctyFilter'].indexOf(child.props.__TYPE) >= 0) {
            return React.cloneElement(child, { __nested: true });
        }
        return child;
    })));
};
Drawer.defaultProps = {
    __TYPE: 'Drawer',
};
export default Drawer;
